/**
 * Upsell Fronts Modal Component
 *
 * Displays a modal after add-to-cart allowing users to add more front color variants.
 * Supports linked Fronts products (via custom.fronts_product metafield) or same-product variants.
 * Works with Lensadvisor via LensAdvizor.postAddToCart callback.
 */

class UpsellFrontsModal extends HTMLElement {
  constructor() {
    super();
    this.addedVariantId = null;
    this.variantData = null;
    this._suppressModal = false;

    // Bind event handlers
    this.handleCartAdd = this.handleCartAdd.bind(this);
    this.handleClick = this.handleClick.bind(this);
    this.handleKeyup = this.handleKeyup.bind(this);
  }

  connectedCallback() {
    // Load variant data
    const dataScript = document.getElementById(`upsell-variants-${this.dataset.productId}`);
    if (dataScript) {
      try {
        this.variantData = JSON.parse(dataScript.textContent);
      } catch (e) {
        console.error('Failed to parse upsell variant data:', e);
      }
    }

    // Listen for standard theme add to cart events (non-Lensadvisor)
    document.addEventListener('on:cart:add', this.handleCartAdd);

    // Intercept cart drawer open after add-to-cart (catches LensAdvisor flow)
    this.interceptCartDrawerOpen();

    // Handle click events (close button, overlay click)
    this.addEventListener('click', this.handleClick);

    // Set up add buttons
    this.querySelectorAll('.upsell-card__add-btn').forEach((btn) => {
      btn.addEventListener('click', (e) => this.handleAddClick(e));
    });
  }

  disconnectedCallback() {
    document.removeEventListener('on:cart:add', this.handleCartAdd);
    this.removeEventListener('click', this.handleClick);
  }

  /**
   * Sets up listeners to show the upsell modal after add-to-cart.
   * Uses cart count comparison to detect new items, which works with
   * LensAdvisor and any other third-party add-to-cart flow.
   */
  interceptCartDrawerOpen() {
    const modal = this;
    this._initialCartCount = null;

    // Get initial cart count for comparison
    fetch('/cart.js')
      .then((r) => r.json())
      .then((data) => {
        modal._initialCartCount = data.item_count;
      })
      .catch(() => {});

    // Intercept cart drawer open if upsell modal is already showing
    document.addEventListener(
      'dispatch:cart-drawer:open',
      (evt) => {
        if (modal.hasAttribute('open')) {
          evt.stopImmediatePropagation();
        }
      },
      true
    );

    // Listen for cart changes to catch LensAdvisor and other third-party flows
    // (standard flow is handled by handleCartAdd via on:cart:add)
    document.addEventListener('on:cart:change', () => {
      if (modal.hasAttribute('open') || !modal.variantData || modal._suppressModal) return;

      fetch('/cart.js')
        .then((r) => r.json())
        .then((data) => {
          const prevCount = modal._initialCartCount;
          modal._initialCartCount = data.item_count;

          // Only trigger if items were added (not removed or updated)
          if (prevCount !== null && data.item_count > prevCount) {
            const variantId = modal.getPageVariantId();
            if (!variantId) return;

            const triggerVariants = modal.variantData.triggerVariants || [];
            if (!triggerVariants.includes(variantId)) return;

            modal.showUpsellModal(variantId);
          }
        })
        .catch(() => {});
    });
  }

  /**
   * Gets the current variant ID from the URL or form input
   * @returns {number|null}
   */
  getPageVariantId() {
    const urlParams = new URLSearchParams(window.location.search);
    let variantId = urlParams.get('variant');
    if (!variantId) {
      const input = document.querySelector('input[name="id"]');
      variantId = input ? input.value : null;
    }
    return variantId ? parseInt(variantId, 10) : null;
  }

  /**
   * Handles the standard theme cart add event
   * @param {CustomEvent} evt - The cart add event
   */
  handleCartAdd(evt) {
    const addedVariantId = evt.detail?.variantId;
    if (!addedVariantId || !this.variantData) return;

    // Check if the added variant belongs to this product (the trigger/frame product)
    const triggerVariants = this.variantData.triggerVariants || [];
    const isThisProduct = triggerVariants.includes(addedVariantId);

    if (!isThisProduct) return;

    // Prevent duplicate handling
    if (this.hasAttribute('open')) return;

    this.showUpsellModal(addedVariantId);
  }

  /**
   * Shows the upsell modal for a given variant
   * Called by Lensadvisor via LensAdvizor.postAddToCart
   * @param {number} variantId - The variant that was added to cart
   */
  showUpsellModal(variantId) {
    // Store the added variant ID
    this.addedVariantId = variantId;

    // If using a linked fronts product, show all fronts (no color to hide).
    // If same product, find the added variant's front color and hide it.
    if (!this.variantData.hasLinkedFrontsProduct) {
      const addedVariant = this.variantData.variants.find(
        (v) => v.id === variantId
      );
      if (addedVariant) {
        this.hideAddedColor(addedVariant.frontColor);
      }
    }

    // Show all cards first (reset from any previous open)
    if (this.variantData.hasLinkedFrontsProduct) {
      this.showAllCards();
    }

    // Check if there are still variants to show
    const visibleCards = this.querySelectorAll('.upsell-card:not([hidden])');
    if (visibleCards.length === 0) {
      // No other fronts to show, just go to cart
      window.location.href = '/cart';
      return;
    }

    // Open the modal
    this.open();
  }

  /**
   * Hides the card for the color that was just added
   * @param {string} frontColor - The front color to hide
   */
  hideAddedColor(frontColor) {
    if (!frontColor) return;
    const colorHandle = frontColor.toLowerCase().replace(/[^a-z0-9]+/g, '-');
    const card = this.querySelector(`.upsell-card[data-color-handle="${colorHandle}"]`);
    if (card) {
      card.setAttribute('hidden', '');
    }
  }

  /**
   * Shows all cards (reset state)
   */
  showAllCards() {
    this.querySelectorAll('.upsell-card[hidden]').forEach((card) => {
      card.removeAttribute('hidden');
    });
  }

  /**
   * Handles click events on the modal
   * @param {Event} evt - Click event
   */
  handleClick(evt) {
    // Close if clicked on overlay or close button
    if (evt.target === this || evt.target.closest('.js-close-modal')) {
      this.close();
      // Suppress modal re-trigger from the on:cart:change fired inside updateCartSections
      this._suppressModal = true;
      this.updateCartSections().then(() => {
        this._suppressModal = false;
      });
    }

    // Open cart drawer if View Cart button clicked
    if (evt.target.closest('.js-open-cart-drawer')) {
      this.close();
      // Suppress modal re-trigger from the on:cart:change fired inside updateCartSections
      this._suppressModal = true;
      this.updateCartSections().then(() => {
        this._suppressModal = false;
        document.dispatchEvent(new CustomEvent('dispatch:cart-drawer:open'));
      });
    }
  }

  /**
   * Handles keyup events for ESC key
   * @param {KeyboardEvent} evt - Keyup event
   */
  handleKeyup(evt) {
    if (evt.key === 'Escape') {
      this.close();
    }
  }

  /**
   * Opens the modal
   */
  open() {
    this.setAttribute('open', '');

    // Trap focus
    if (typeof trapFocus === 'function') {
      trapFocus(this);
    }

    // Pause media
    if (typeof window.pauseAllMedia === 'function') {
      window.pauseAllMedia();
    }

    // Add ESC key listener
    this.addEventListener('keyup', this.handleKeyup);

    // Dispatch open event
    this.dispatchEvent(new CustomEvent('upsell-modal:open', { bubbles: true }));
  }

  /**
   * Closes the modal
   */
  close() {
    this.removeAttribute('open');

    // Remove focus trap
    if (typeof removeTrapFocus === 'function') {
      removeTrapFocus();
    }

    // Remove ESC key listener
    this.removeEventListener('keyup', this.handleKeyup);

    // Reset button states after close
    this.resetButtonStates();

    // Dispatch close event
    this.dispatchEvent(new CustomEvent('upsell-modal:close', { bubbles: true }));
  }

  /**
   * Handles click on individual add buttons
   * @param {Event} evt - Click event
   */
  async handleAddClick(evt) {
    const btn = evt.currentTarget;
    const variantId = btn.dataset.variantId;

    if (!variantId || btn.disabled || btn.classList.contains('is-loading')) {
      return;
    }

    // Set loading state
    btn.classList.add('is-loading');
    const addText = btn.querySelector('.upsell-card__add-text');
    const addedText = btn.querySelector('.upsell-card__added-text');
    const loadingIcon = btn.querySelector('.upsell-card__loading');

    if (addText) addText.hidden = true;
    if (loadingIcon) loadingIcon.hidden = false;

    try {
      // Add to cart via AJAX
      const response = await fetch(theme.routes.cartAdd, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Accept: 'application/json',
        },
        body: JSON.stringify({
          id: parseInt(variantId, 10),
          quantity: 1,
        }),
      });

      const data = await response.json();

      if (response.ok) {
        // Success state
        btn.classList.remove('is-loading');
        btn.classList.add('is-success');
        if (addedText) addedText.hidden = false;
        if (loadingIcon) loadingIcon.hidden = true;

        // Update cart count in header
        this.updateCartSections();

        // Dispatch custom event
        this.dispatchEvent(
          new CustomEvent('upsell:item-added', {
            bubbles: true,
            detail: { variantId: parseInt(variantId, 10) },
          })
        );

        // Reset button after delay (so user can add more)
        setTimeout(() => {
          btn.classList.remove('is-success');
          if (addText) addText.hidden = false;
          if (addedText) addedText.hidden = true;
        }, 2000);
      } else {
        // Error state
        throw new Error(data.description || 'Failed to add to cart');
      }
    } catch (error) {
      console.error('Upsell add to cart error:', error);
      btn.classList.remove('is-loading');
      if (addText) addText.hidden = false;
      if (loadingIcon) loadingIcon.hidden = true;

      // Show error briefly
      btn.classList.add('is-error');
      setTimeout(() => btn.classList.remove('is-error'), 2000);
    }
  }

  /**
   * Updates cart sections after adding an item
   */
  async updateCartSections() {
    try {
      const response = await fetch(
        `${theme.routes.cart}?sections=page-header,cart-drawer`
      );
      const data = await response.json();

      // Update header cart count
      if (data['page-header']) {
        const tempDiv = document.createElement('div');
        tempDiv.innerHTML = data['page-header'];
        const newCartCount = tempDiv.querySelector('.js-cart-count');
        const currentCartCount = document.querySelector('.js-cart-count');
        if (newCartCount && currentCartCount) {
          currentCartCount.innerHTML = newCartCount.innerHTML;
        }
      }

      // Update cart drawer if present
      if (data['cart-drawer']) {
        const cartDrawer = document.querySelector('cart-drawer');
        if (cartDrawer && cartDrawer.updateFromCartChange) {
          cartDrawer.updateFromCartChange(data['cart-drawer']);
        }
      }

      // Dispatch cart change event
      document.dispatchEvent(new CustomEvent('on:cart:change', { bubbles: true }));
    } catch (error) {
      console.error('Failed to update cart sections:', error);
    }
  }

  /**
   * Resets all button states
   */
  resetButtonStates() {
    this.querySelectorAll('.upsell-card__add-btn').forEach((btn) => {
      btn.classList.remove('is-loading', 'is-success', 'is-error');
      const addText = btn.querySelector('.upsell-card__add-text');
      const addedText = btn.querySelector('.upsell-card__added-text');
      const loadingIcon = btn.querySelector('.upsell-card__loading');

      if (addText) addText.hidden = false;
      if (addedText) addedText.hidden = true;
      if (loadingIcon) loadingIcon.hidden = true;
    });
  }
}

customElements.define('upsell-fronts-modal', UpsellFrontsModal);
